export const UNSUCCESSFUL_REASONS = [
  'FEIN does not match Enterprise Legal Name',
  'Enterprise name cannot be legally associated with Enterprise Website URL',
  'POC name does not match POC email or cannot be confirmed as employee',
  'Financial reference failure — unable to confirm business/banking references',
  'Legal complaints — TCPA/TSR violations or fraud-related judgments',
  'SHAFT content detected in Call Logo, Call Reason, or Display Name',
  'IP rights unconfirmed — unable to verify trademark/copyright ownership',
  'TN association failure — submitted TNs not confirmed with Registrant',
  'LOA verification failure — authorization could not be validated',
  'Trust criteria not met — compliance concerns identified',
  'Non-responsive registrant — failed to provide required information',
] as const;

export const BCID_APPROVED_REASONS_URL = 'https://brandedcallingid.com/brand-guidelines/#CallReasons';

export interface KycSource {
  name: string;
  url: string | null;
  description: string;
}

// #772 — WHAT IT TAKES TO VERIFY AN ITEM. The box cannot be ticked, by hand or
// by the auto-verify on a clear record, until the item has at least
// `minRecords` evidence records from at least `minSources` different sources
// (compared trimmed and case-insensitive, so "PACER" filed twice as "pacer "
// is one source). Legal Complaints is 2 from 2, so the same search filed twice
// cannot satisfy it; every other item is 1 from 1.
//
// `counts: 'financialRefs'` is Financial Stability: what is counted there is
// the financial references 382 has verified (#695), not evidence records, and
// a reference carries no source, so `minSources` is 0.
//
// Cases that had entered vetting before #772 shipped are grandfathered
// (`kycGrandfathered`, set by A028) and keep the old rule: see
// kyc-progress.ts.
//
// The server holds the same rule for the required items, on approve-direction
// decisions, in apps/api/src/lib/kyc-items.ts. The packages share no module,
// so the copies are tied by disk-read tests on both sides
// (docs/CROSS_PACKAGE_CONSTANTS.md), and those tests read the fields in this
// order: required, minRecords, minSources, counts.
export interface KycItem {
  label: string;
  sources: KycSource[];
  required: boolean;
  minRecords: number;
  minSources: number;
  counts: 'evidence' | 'financialRefs';
}

export const KYC_ITEMS: Record<string, KycItem> = {
  legalName: {
    label: 'Legal Name & Jurisdiction',
    sources: [
      { name: 'Secretary of State', url: null, description: 'State SOS business entity search' },
      { name: 'IRS EIN Verification', url: null, description: 'FEIN matches legal entity name' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  dba: {
    label: 'DBAs / Trade Names',
    sources: [
      { name: 'Secretary of State', url: null, description: 'DBA filings search' },
      { name: 'County Clerk Records', url: null, description: 'Local DBA registrations' },
    ],
    required: false,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  website: {
    label: 'Website & Domain Verification',
    sources: [
      { name: 'ICANN WHOIS', url: 'https://lookup.icann.org/en', description: 'Domain registration and ownership lookup' },
      { name: 'Website Review', url: null, description: 'Active site, matches business, email domain matches' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  orgType: {
    label: 'Organization Type & Address',
    sources: [
      { name: 'Secretary of State', url: null, description: 'Business type (LLC, Corp, etc.) and registered agent' },
      { name: 'Google Maps', url: null, description: 'Physical address verification' },
      { name: 'USPS Address Validation', url: null, description: 'Mailing address confirmation' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  financial: {
    label: 'Financial Stability',
    sources: [
      { name: 'DUNS / D&B', url: null, description: 'Dun & Bradstreet business credit report' },
      { name: 'FEIN Verification', url: null, description: 'Federal EIN confirmation' },
      { name: 'Business References (min 2)', url: null, description: 'Verbal/written references from business partners' },
      { name: 'Banking Reference (min 1)', url: null, description: 'Bank confirms account in good standing' },
      { name: 'Audited Financial Statements', url: null, description: 'Alternative to references if available' },
    ],
    required: true,
    minRecords: 1,
    minSources: 0,
    counts: 'financialRefs',
  },
  legal: {
    label: 'Legal Complaints (5-Year Lookback)',
    sources: [
      { name: 'PACER', url: 'https://pcl.uscourts.gov/pcl/index.jsf', description: 'Federal court case search — TCPA/TSR violations' },
      { name: 'FTC Case Search', url: 'https://www.ftc.gov/legal-library/browse/cases-proceedings', description: 'FTC enforcement actions and complaints' },
      { name: 'FCC ECFS', url: 'https://www.fcc.gov/ecfs/', description: 'FCC complaints database' },
      { name: 'State AG Complaints', url: null, description: 'State attorney general consumer complaint databases' },
      { name: 'BBB', url: 'https://www.bbb.org/', description: 'Better Business Bureau complaint history' },
    ],
    required: true,
    minRecords: 2,
    minSources: 2,
    counts: 'evidence',
  },
  billing: {
    label: 'Billing Contact & Address',
    sources: [
      { name: 'Direct Verification', url: null, description: 'Confirmed billing contact name, email, phone, and mailing address' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  authorizer: {
    label: 'Authorizer Verification',
    sources: [
      { name: 'Email Domain Match', url: null, description: 'Authorizer email matches company domain' },
      { name: 'LinkedIn / Company Directory', url: null, description: 'Confirm authorizer is employee/officer of registrant' },
      { name: 'LOA (if agent)', url: null, description: 'Letter of Authorization if authorizer is acting as agent' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  sic: {
    label: 'SIC Code',
    sources: [
      { name: 'SIC Code Lookup', url: 'https://siccode.com/sic-code-lookup-directory', description: 'Verify primary business domain' },
    ],
    required: true,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
  profLicense: {
    label: 'Professional License',
    sources: [
      { name: 'State Licensing Board', url: null, description: 'Professional/occupational license verification if applicable' },
    ],
    required: false,
    minRecords: 1,
    minSources: 1,
    counts: 'evidence',
  },
};

export const STRIKE_THRESHOLDS = {
  VALID_CLAIMS: 3,
  MISREPRESENTATIONS: 2,
  FRAUDULENT_INTENT: 1,
} as const;

// THE BCID PRE-APPROVED CALL REASONS (#541) — 45 values, supplied by the product
// owner in the 2026-08-27 review session.
//
// The BCBP is explicit: "The Call Reason shall be selected from values that have
// been registered and vetted by the Vetting and Validation Agent", with custom
// reasons permitted subject to a character limit. Before #541 the only guardrail
// was a 15-item array inlined in a render function in OnboardingTab.tsx whose
// provenance nobody could vouch for, and every one of the three call-reason
// surfaces was free text. This array replaced it; there is deliberately no
// second copy in this workspace.
//
// BYTE-EXACT, and one value turns on that. `Today’s Delivery` carries U+2019
// (RIGHT SINGLE QUOTATION MARK), not an ASCII apostrophe. What ships to CTIA as
// `default_crn` is these bytes, so a transliteration to U+0027 would make our
// registered value differ from the approved one — and it is invisible in a diff,
// which is why OnboardingTab.test.tsx asserts the CODEPOINT rather than trusting the
// eye. Do not retype this list; copy it.
//
// Not admin-editable yet: CTIA revises the list over time and a code deploy per
// reason is friction, but that is a tracked follow-up, not this constant.
//
// THIS IS THE CANONICAL COPY. `apps/portal/src/lib/validation.ts` carries a
// MIRROR of it, because @bcid/spa and @bcid/portal are separate workspace
// packages and the portal's tsconfig is `composite` with `include: ["src"]`, so
// it cannot import from here without failing `tsc -b`. The two are pinned to
// each other by a test that reads THIS FILE off disk:
//
//   apps/portal/src/lib/validation.test.ts
//   it("matches the SPA's BCID_APPROVED_CALL_REASONS byte for byte")
//
// Edit both copies in the same commit, or that test fails by name. Reformatting
// the entries below to double quotes or a template literal would blind its
// extraction, which is why it also asserts the extracted count is exactly 45.
export const BCID_APPROVED_CALL_REASONS = [
  'Account Alert',
  'Account Notification',
  'Account Services',
  'Accounting Department',
  'Agent Call Back',
  'Appointment Confirmation',
  'Appointment Reminder',
  'Confirm Appointment',
  'Confirm Card Use',
  'Confirm Order',
  'Confirm Receipt',
  'Confirm Status',
  'Customer Service',
  'Delivery ETA',
  'Flight Arrival',
  'Flight Boarding',
  'Flight Cancellation',
  'Flight Delay',
  'Follow-Up Calls',
  'Fraud Alert',
  'General Support',
  'Identity Verification',
  'Investor Relations',
  'Item Ready',
  'Order Confirmation',
  'Order Ready',
  'Policy Update',
  'Recall',
  'Reschedule Request',
  'Reservation Ready',
  'Responding to Your Inquiry',
  'Response Requested',
  'Returning Your Call',
  'Rx Ready',
  'Sales Call',
  'School Closing Alert',
  'Service Outage Notification',
  'Snow Alert',
  'Technical Support',
  'Today’s Delivery',
  'Upcoming Delivery',
  'Verification Code',
  'Weather Alert',
  'Your Account',
  'Your Order',
] as const;

// The BCBP character cap on a CUSTOM call reason. Pre-approved values are NOT
// measured against this — they may run to the CTIA Platform's 64-character
// `default_crn` width (CTIA_ONBOARDING_LIMITS.callReason). Every approved value
// above is <=27 characters, so in practice this binds only on custom values;
// OnboardingTab.test.tsx asserts that, so a future list edit that breaks the
// assumption is caught rather than silently capping an approved value.
export const CUSTOM_CALL_REASON_MAX = 35;

/**
 * Whether `value` is one of the pre-approved reasons.
 *
 * EXACT match, deliberately. What reaches CTIA is the stored bytes, and
 * `default_crn` has to equal a value the VA actually registered — `ACCOUNT
 * ALERT` and `Today's Delivery` (ASCII apostrophe) are not those values, and
 * the second is the exact drift this list is byte-pinned against. The inline
 * array this replaced compared case-insensitively, which could only ever have
 * been a guess at intent; now that all three surfaces write through a dropdown,
 * a case-variant cannot be PRODUCED by the UI at all — it can only be legacy
 * stored data, which is precisely what should reach an analyst as custom.
 */
export function isApprovedCallReason(value: string): boolean {
  return (BCID_APPROVED_CALL_REASONS as readonly string[]).includes(value);
}

// THE PERSONAL EMAIL DOMAINS THE AUTHORIZER MAY NOT USE (#518, #635).
//
// A MIRROR. `PERSONAL_EMAIL_DOMAINS` in `apps/api/src/routes/intake.ts` is the
// CONTRACT and the authority; this is the staff desk's copy, guarding
// staff-entered data on the Onboarding tab's submit. @bcid/spa and @bcid/api are
// separate workspace packages with no shared module, so it is a copy rather than
// an import — the arrangement docs/CROSS_PACKAGE_CONSTANTS.md exists to record.
//
// PINNED to the API's list by a test that reads that file off disk:
//
//   apps/spa/src/pages/CaseDetail/tabs/OnboardingTab.test.tsx
//   it("PERSONAL_EMAIL_DOMAINS matches the copy in apps/api/src/routes/intake.ts")
//
// Edit both copies (and the portal's third, in apps/portal/src/lib/validation.ts)
// in the same commit, or that test fails by name. Reformatting the entries below
// to double quotes would blind its extraction, which is why it also asserts the
// extracted count is exactly 7.
//
// Until #635 this list was an inline array literal inside the submit gate, which
// is why it went unpinned while the portal's copy did not: there was nothing for
// a disk-read pin to extract. Naming it is what made the pin possible.
//
// One thing this pin deliberately does NOT claim: that the three copies apply
// the list the same WAY. The API and the portal anchor on the `@`, so
// `ada@notgmail.com` is a work address to them; the staff gate matches a bare
// suffix and refuses it. That difference predates #635 and changing it is a
// behaviour change, not a pin.
export const PERSONAL_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'aol.com', 'icloud.com', 'protonmail.com',
];

// #770 — the CTIA registrant class, labelled. Staff used to see a bare `A`, `B`
// or `C` where the customer had chosen from these exact words, and on staff
// cases a free-text value ("LLC") that the settlement worker had to guess from.
//
// Appended at the END of this file on purpose: docs/compliance/bcbp-conformance.md
// cites lines above by number, and an insertion higher up would move them.
//
// A MIRROR. `ORG_TYPES` in `apps/portal/src/pages/SubmissionForm.tsx` is the
// canonical copy (itself pinned to the intake API's enum description), and its
// labels are what the customer picked from. PINNED to it by a test that reads
// that file off disk:
//
//   apps/spa/src/domain/constants.test.ts
//   it("ORG_TYPE_LABELS matches the portal's ORG_TYPES, read out of its source")
//
// Edit both copies in the same commit, or that test fails by name.
export const ORG_TYPE_LABELS: ReadonlyArray<{ value: 'A' | 'B' | 'C'; label: string }> = [
  { value: 'A', label: 'A — Commercial Enterprise' },
  { value: 'B', label: 'B — Government/Public Service' },
  { value: 'C', label: 'C — Public Charity/Non-profit' },
];

// The label for a stored org type. Anything that is not one of the three codes
// — a legacy free-text value from before the staff field became a select — is
// returned verbatim, so a case still shows what was entered until it is edited.
export function formatOrgType(value: unknown): string {
  if (value === undefined || value === null) return '';
  const text = String(value);
  return ORG_TYPE_LABELS.find(o => o.value === text)?.label ?? text;
}
